import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getItemWithExpiry,
  setItemWithExpiry,
  showToast,
  skipNullValue,
} from "../../lib/utility";
import axios from "../../redux/axios";
import { handleDownloadSizeProgress } from "../../Layouts/Navbar/DownloadProgress/DownloadLogSlice";

const reportRequestConfig = (controller, download, onDownloadProgress) => {
  if (!controller?.signal) return {};
  return {
    signal: controller.signal,
    ...(download && onDownloadProgress ? { onDownloadProgress } : {}),
  };
};


const normalizeCoverageReportResponse = (responseData) => {
  if (Array.isArray(responseData)) {
    return {
      data: responseData,
      currentPage: 0,
      totalPages: 1,
      totalItems: responseData.length,
    };
  }

  const rows = Array.isArray(responseData?.data) ? responseData.data : [];

  return {
    data: rows,
    currentPage: responseData?.currentPage ?? 0,
    totalPages: responseData?.totalPages ?? 1,
    totalItems: responseData?.totalItems ?? rows.length,
  };
};

const initialState = {
  isFetching: false,

  batchUser: [],
  isFetchingBatch: false,
  isSuccessBatchUser: false,
  isErrorBatchUser: false,
  errorMessageBatchUser: "",

  abortedUser: [],
  isFetchingAborted: false,
  isSuccessAbortedUser: false,
  isErrorAbortedUser: false,
  errorMessageAbortedUser: "",

  deliverySummaryData: [],
  isFetchingDeliverySummary: false,
  isSuccessDeliverySummaryData: false,
  isErrorDeliverySummaryData: false,
  errorMessageDeliverySummaryData: "",

  contentUser: [],
  cachedGraphData: null, 
  isFetchingContentUser: false,
  isSuccessContentUser: false,
  isErrorContentUser: false,
  errorMessageContentUser: "",

  advanceUser: [],
  isFetchingAdvance: false,
  isSuccessAdvanceUser: false,
  isErrorAdvanceUser: false,
  errorMessageAdvanceUser: "",

  scheduleUser: [],
  isFetchingScheduleUser: false,
  isSuccessScheduleUser: false,
  isErrorScheduleUser: false,
  errorMessageScheduleUser: "",

  latencyUser: [],
  isFetchingLatencyUser: false,
  isSuccessLatencyUser: false,
  isErrorLatencyUser: false,
  errorMessageLatencyUser: "",

  lookupUser: [],
  isFetchingLookupUser: false,
  isSuccessLookupUser: false,
  isErrorLookupUser: false,
  errorMessageLookupUser: "",

  submissionUser: [],
  isFetchingSubmissionUser: false,
  isSuccessSubmissionUser: false,
  isErrorSubmissionUser: false,
  errorMessageSubmissionUser: "",

  consumptionUser: [],
  isFetchingConsumptionUser: false,
  isSuccessConsumptionUser: false,
  isErrorConsumptionUser: false,
  errorMessageConsumptionUser: "",

  campaignUser: [],
  isFetchingCampaignUser: false,
  isSuccessCampaignUser: false,
  isErrorCampaignUser: false,
  errorMessageCampaignUser: "",

  smscData: [],
  isFetchingSmscData: false,
  isSuccessSmscData: false,
  isErrorSmscData: false,
  errorMessageSmscData: "",

  aggregatedUser: {
    totalTRN: 0,
    totalOTP: 0,
    totalMKT: 0,
    aggregators: [],
  },
  isFetchingAggregatedUser: false,
  isSuccessAggregatedUser: false,
  isErrorAggregatedUser: false,
  errorMessageAggregatedUser: "",

  isCountryListFetching: false,
  isOperatorListFetching: false,
  isCOperatorListFetching: false,

  profitData: [],
  isFetchingProfitData: false,
  isSuccessProfitData: false,
  isErrorProfitData: false,
  errorMessageProfitData: "",

  performanceUserData: [],
  isFetchingPerformanceUserData: false,
  isSuccessPerformanceUser: false,
  isErrorPerformanceUser: false,
  errorMessagePerformanceUser: "",

  performanceRouteData: [],
  isFetchingPerformanceRouteData: false,
  isSuccessPerformanceRoute: false,
  isErrorPerformanceRoute: false,
  errorMessagePerformanceRoute: "",

  coverageData: [],
  isFetchingCoverageData: false,
  isSuccessCoverage: false,
  isErrorCoverage: false,
  errorMessageCoverage: "",

  blockedData: [],
  isFetchingBlockedData: false,
  isSuccessBlocked: false,
  isErrorBlocked: false,
  errorMessageBlocked: "",

  smscListData: [],
  isFetchingSmscList: false,
  isSuccessSmscList: false,
  isErrorSmscList: false,
  errorMessageSmscList: "",

  TPSData: [],
  isFetchingTPSData: false,
  isSuccessTPS: false,
  isErrorTPS: false,
  errorMessageTPS: "",

  userMarginData: [],
  isFetchingUserMarginData: false,

  edrData: [],
  isFetchingEdrData: false,

  resumeTrafficData: [],
  isFetchingResumeTrafficData: false,

  isFetchingSaveFilter: false,
};

export const batchUserData = createAsyncThunk(
  "reports/summary-report-view",
  async (
    { udata, search, page, size, download = false, controller, firstTime = false },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);

      const requestBody = {
        ...udata,
        firstTime: firstTime,
        ...(firstTime ? {} : { totalCount: udata.totalCount || 0 }) 
      };

      const { data, status } = await axios.post(
        `/reports/summary-report?` + urlParams.toString(),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );
      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status, download, firstTime };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const abortedUserData = createAsyncThunk(
  "reports/abort-batch",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      const tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);

      const { data, status } = await axios.post(
        `/reports/abort-batch?` + urlParams.toString(),
        udata,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );
      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const getAbortedNumbers = createAsyncThunk(
  "reports/aborted-numbers",
  async (id, thunkAPI, download = false) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(
        `/sms/abortedNumberList?abort_batch_id=${id}`,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const deliverySummaryUserData = createAsyncThunk(
  "reports/dlr-summary-report",
  async (
    { udata, search, page, size, download = false, controller, firstTime = false },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 10);
      
      const requestBody = {
        startDate: udata.startDate,
        endDate: udata.endDate,
        ...(udata.toGmt && { toGmt: udata.toGmt }), 
        ...(udata.clientId && { clientId: udata.clientId }),
        ...(udata.senderId && { senderId: udata.senderId }),
        ...(udata.country && { country: udata.country }),
        ...(udata.operator && { operator: udata.operator }),
        ...(udata.destinationNumber && { destinationNumber: udata.destinationNumber }),
        firstTime: firstTime
      };
 
      
      const { data, status } = await axios.post(
        "/reports/dlr-summary-report?" + urlParams.toString(),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );
      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const contentUserData = createAsyncThunk(
  "reports/content-report",
  async (
    { udata, search, page, size, download = false, controller, firstTime = false, totalCount = 0 },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);

      const requestBody = {
        startDate: udata.startDate,
        endDate: udata.endDate,
        ...(udata.toGmt && { toGmt: udata.toGmt }), 
        ...(udata.clientId && { clientId: udata.clientId }),
        ...(udata.senderId && { senderId: udata.senderId }),
        ...(udata.country && { country: udata.country }),
        ...(udata.operator && { operator: udata.operator }),
        ...(udata.destinationNumber && { destinationNumber: udata.destinationNumber }),
        firstTime,
        totalCount
      };


      const { data, status } = await axios.post(
        "/reports/content-report?" + urlParams.toString(),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );
      
      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status, firstTime };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const advanceUserData = createAsyncThunk(
  "reports/advance",
  async (
    { udata, search, page, size, download = false, controller, firstTime = false },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);

      const requestBody = {
        ...udata,
        firstTime: firstTime,
        // Match page-fetch contract: when not firstTime, send totalCount so download/server don't hang
        ...(firstTime
          ? {}
          : { totalCount: udata.totalCount ?? 0 }),
      };

      const { data, status } = await axios.post(
        "/reports/advance-report?" + urlParams.toString(),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );
      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const scheduleUserData = createAsyncThunk(
  "reports/schedule-report",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      
      const urlParams = new URLSearchParams();
      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);
      const { data, status } = await axios.post(
        "/reports/schedule-report?" + urlParams.toString(),
        skipNullValue(udata),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const lookupUserData = createAsyncThunk(
  "reports/lookup-report",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const urlParams = new URLSearchParams();
      if (search) urlParams.append("searchParameter", search);

    
      const requestBody = skipNullValue({ ...udata, page, size });
      if (requestBody?.batchId === "%" || requestBody?.batchId === "") {
        delete requestBody.batchId;
      }

      const { data, status } = await axios.post(
        "/hlr/hlr-report?" + urlParams.toString(),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                 
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const latencyUserData = createAsyncThunk(
  "reports/latency-report",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("search", search);

      const requestData = {
        startDate: udata.startDate,
        endDate: udata.endDate,
        ...(udata.toGmt && { toGmt: udata.toGmt }), 
        ...(udata.usernames && { usernames: udata.usernames }),
        ...(udata.networkId && { networkId: udata.networkId }),
        ...(udata.networkIds && { networkIds: udata.networkIds }),
        ...(udata.senderId && { senderId: udata.senderId }),
        ...(udata.smscnames && { smscnames: udata.smscnames }),
        ...(udata.allSelected !== undefined && { allSelected: udata.allSelected }),
      };

      console.log("Latency Report API Request:", requestData); 

      const { data, status } = await axios.post(
        "/reports/latency-report?" + urlParams.toString(),
        skipNullValue(requestData), 
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
       
        return data;
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);

export const submissionUserData = createAsyncThunk(
  "reports/submission-report",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let query = udata.search ? "?searchParameter=" + udata.search : "";
      const { data, status } = await axios.post(
        "/reports/submission-execute" + query,
        udata,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const consumptionUserData = createAsyncThunk(
  "reports/balance-report",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let query = udata.search ? "?searchParameter=" + udata.search : "";
      
      const requestData = {
        startDate: udata.startDate,
        endDate: udata.endDate,
        ...(udata.toGmt && { toGmt: udata.toGmt }), 
        ...(udata.clientId && { clientId: udata.clientId }),
        ...(udata.country && { country: udata.country }),
        ...(udata.operator && { operator: udata.operator }),
        reportType: udata.reportType,
        
      };

      console.log("Consumption Report API Request:", requestData); 

      const { data, status } = await axios.post(
        "/reports/balance-report" + query,
        skipNullValue(requestData), 
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return {
          status,
          data: Object.keys(data).reduce((acc, key) => {
            return [...acc, ...data[key]];
          }, []),
        };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const aggregatedReportData = createAsyncThunk(
  "report/aggregated",
  async (
    { udata, search, page, size, download = false, controller, firstTime = false },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();

      let urlParam = new URLSearchParams();
      urlParam.append("startDate", udata.startDate);
      urlParam.append("endDate", udata.endDate);
      
      if (udata.toGmt) {
        urlParam.append("toGmt", udata.toGmt);
      }
      
      search && urlParam.append("searchParameter", search);
      urlParam.append("page", page || 0);
      urlParam.append("size", size || 20);

      urlParam.append("firstTime", firstTime);

      if (udata.totalCount !== undefined && udata.totalCount !== null) {
        urlParam.append("totalCount", udata.totalCount);
      }

      if (udata.systemId) {
        urlParam.append("systemId", udata.systemId);
      }

      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(
        "/reports/aggregator-report?" + urlParam.toString(),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);
export const campaignUserData = createAsyncThunk(
  "/reports/campaign-report",
  async ({ udata, sortBy, sortDir }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let queryString = udata?.search ? "search=" + udata.search : "";
      const { data, status } = await axios.post(
        `/reports/campaign-report?` + queryString,
        skipNullValue({
          ...udata,
          sortBy,
          sortDir,
        }),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
           
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const getSmscData = createAsyncThunk(
  "/reports/delivery-smsc",
  async ({ udata, download = false, controller }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let queryString = udata.search ? "searchParameter=" + udata.search : "";
      const { data, status } = await axios.post(
        `/reports/smsc-dlr-report?` + queryString,
        skipNullValue(udata),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                 
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const getSmscList = createAsyncThunk(
  "reports/smsc-list",
  async (udata, thunkAPI) => {
    try {
      const smscData = getItemWithExpiry("smscList");
      if (smscData) {
        return { ...smscData, status: 200 };
      }

      const { auth } = thunkAPI.getState();
      const tokenStr = auth.user.accessToken;

      const { data, status } = await axios.get(
        `routes/user-list?purpose=${udata}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        setItemWithExpiry("smscList", data, 10800);
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const getProfitData = createAsyncThunk(
  "reports/profit",
  async (
    { udata, search, page, size, download = false, controller, firstTime = false },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);

      const requestBody = {
        ...skipNullValue(udata),
        firstTime: firstTime,
        ...(firstTime ? {} : { totalCount: udata.totalCount || 0 }) 
      };

      const { data, status } = await axios.post(
        `/reports/profit?` + urlParams.toString(),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status, download, firstTime };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);
export const getPerformanceUserData = createAsyncThunk(
  "reports/performance-user",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();
      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);
      const { data, status } = await axios.post(
        `/reports/performance/user-delivery?${urlParams.toString()}`,
        skipNullValue(udata),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
              
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);

export const getPerformanceRouteData = createAsyncThunk(
  "reports/performance-route",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();
      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);
      
      const requestData = {
        startDate: udata.startDate,
        endDate: udata.endDate,
        ...(udata.toGmt && { toGmt: udata.toGmt }), 
        ...(udata.country && { country: udata.country }),
        ...(udata.operator && { operator: udata.operator }),
        ...(udata.groupBy && { groupBy: udata.groupBy }),
        ...(udata.smscnames && { smscnames: udata.smscnames }),
      };

      console.log("Performance Route API Request:", requestData); 

      const { data, status } = await axios.post(
        `/reports/performance-route?${urlParams.toString()}`,
        skipNullValue(requestData), 
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...reportRequestConfig(controller, download, (progressEvent) => {
            const downloadedBytes = progressEvent.loaded;
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: downloadedBytes * 1.05,
              })
            );
          }),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const getBlockedData = createAsyncThunk(
  "reports/blocked-report",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);
      const { data, status } = await axios.post(
        `/reports/blocked-report?` + urlParams.toString(),
        skipNullValue(udata),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
              
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);
export const getTPSData = createAsyncThunk(
  "reports/TPS-report",
  async (
    { udata, search, page, size, download = false, controller },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (search) urlParams.append("searchParameter", search);
      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);
      const { data, status } = await axios.post(
        `/reports/TPS?` + urlParams.toString(),
        skipNullValue(udata),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...reportRequestConfig(controller, download, (progressEvent) => {
            const downloadedBytes = progressEvent.loaded;
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: downloadedBytes * 1.05,
              })
            );
          }),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);
export const getCoverageUserData = createAsyncThunk(
  "reports/coverage",
  async (
    {
      udata,
      search,
      page = 0,
      size = 10,
      sortBy,
      sortDirection = "asc",
      download = false,
      controller,
    },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const queryParams = new URLSearchParams();
      if (udata?.userIds) {
        queryParams.append(
          "userIds",
          Array.isArray(udata.userIds)
            ? udata.userIds.join(",")
            : udata.userIds
        );
      }
      if (udata?.country) queryParams.append("country", udata.country);
      if (udata?.operator) queryParams.append("operator", udata.operator);
      if (search) queryParams.append("searchParameter", search);
      if (sortBy) queryParams.append("sortBy", sortBy);
      queryParams.append("sortDirection", sortDirection || "asc");
      queryParams.append("pageNo", page ?? 0);
      queryParams.append("pageSize", size ?? 10);

      const { data, status } = await axios.get(
        `/reports/coverage-report?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...reportRequestConfig(controller, download, (progressEvent) => {
            const downloadedBytes = progressEvent.loaded;
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: downloadedBytes * 1.05,
              })
            );
          }),
        }
      );

      const dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        const normalized = normalizeCoverageReportResponse(data);

        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }

        return { ...normalized, status };
      }

      return thunkAPI.rejectWithValue({ ...data, status });
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);
export const getEdrData = createAsyncThunk(
  "reports/edr-data",
  async (
    {
      udata,
      search,
      page,
      size,
      isFirstTime,
      totalCount,
      download = false,
      controller,
    },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      const requestData = {
        startDate: udata.startDate,
        endDate: udata.endDate,
        ...(udata.toGmt && { toGmt: udata.toGmt }), 
        ...(udata.usernames && { usernames: udata.usernames }),
        ...(udata.destination && { destination: udata.destination }),
        ...(udata.source && { source: udata.source }),
        ...(udata.messageId && { messageId: udata.messageId }),
        pageSize: size,
        pageNo: page,
        firstTime: isFirstTime,
        ...(!isFirstTime ? { totalCount } : {}),
      };

      console.log("EDR Report API Request:", requestData); 

      const { data, status } = await axios.post(
        `/reports/edr-report?` + urlParams.toString(),
        {
          ...skipNullValue(requestData),
          ...(search ? { searchParameter: search } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...reportRequestConfig(controller, download, (progressEvent) => {
            const downloadedBytes = progressEvent.loaded;
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: downloadedBytes * 1.05,
              })
            );
          }),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);
export const getResumeTrafficData = createAsyncThunk(
  "reports/vendor-summary",
  async (
    {
      udata,
      page,
      size,
      isFirstTime,
      totalCount,
      download = false,
      controller,
    },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      const { data, status } = await axios.post(
        `/reports/resume-traffic?` + urlParams.toString(),
        skipNullValue({
          ...udata,
          pageSize: size,
          pageNo: page,
          firstTime: isFirstTime,
          ...(!isFirstTime ? { totalCount } : {}),
         
        }),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
          ...reportRequestConfig(controller, download, (progressEvent) => {
            const downloadedBytes = progressEvent.loaded;
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: downloadedBytes * 1.05,
              })
            );
          }),
        }
      );

      let dataLength = JSON.stringify(data).length;
      if (status.toString().startsWith("2")) {
        if (download) {
          setTimeout(() => {
            thunkAPI.dispatch(
              handleDownloadSizeProgress({
                id: download,
                size: dataLength * 1.05,
              })
            );
          }, 450);
        }
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      console.error("err ===>", err);
      if (err.name === "CanceledError" && err.code === "ERR_CANCELED") {
        showToast("Download Cancelled", "success");
        return thunkAPI.rejectWithValue({ message: "Download Canceled" });
      }
      if (!err.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);
export const getCountryList = createAsyncThunk(
  "/reports/country",
  async (udata, thunkAPI) => {
    try {
      const countryList = getItemWithExpiry("countryList");
      if (countryList) {
        return { data: countryList, status: 200 };
      }
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(`/reports/all-countries`, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
        },
      });

      if (status.toString().startsWith("2")) {
        setItemWithExpiry("countryList", data, 10800);
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getOperatorList = createAsyncThunk(
  "/reports/operators",
  async (udata, thunkAPI) => {
    try {
      const operatorsList = getItemWithExpiry("operatorsList");
      if (operatorsList) {
        return { data: operatorsList, status: 200 };
      }
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(`/reports/all-operators`, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
        },
      });

      if (status.toString().startsWith("2")) {
        setItemWithExpiry("operatorsList", data, 10800);
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getOperatorByCountryList = createAsyncThunk(
  "/reports/operators-by-country",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(`/reports/operators/` + udata, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
        },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getDashboardPerformanceList = createAsyncThunk(
  "/reports/dashboard-performance-list",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.post(
        `/reports/dashboard/list`,
        {},
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getDashboardTrafficList = createAsyncThunk(
  "/reports/dashboard-traffic-list",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(`/reports/traffic/list`, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
        },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getCampaignList = createAsyncThunk(
  "/reports/campaignList",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.get(`/reports/`, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
        },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);


export const saveFilter = createAsyncThunk(
  "/reports/saveFilter",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.post(
        `/reports/recent-filters`,
        udata,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getSavedFilter = createAsyncThunk(
  "/reports/getSavedFilter",
  async (purposeType, thunkAPI) => {
    console.log("purposeType value:", purposeType);
    console.log("purposeType type:", typeof purposeType);

    const { auth } = thunkAPI.getState();
    const tokenStr = auth.user.accessToken;

    const purposeTypeString = typeof purposeType === 'string' 
      ? purposeType 
      : purposeType?.toString() || '';

    const response = await axios.get("/reports/recent-filters", {
      params: {
        purposeType: purposeTypeString,
      },
      headers: {
        Authorization: `Bearer ${tokenStr}`,
      },
    });

    
    const looksLikeFilter = (obj) =>
      obj &&
      typeof obj === "object" &&
      ("recentFilterName" in obj || "specialAttributes" in obj);

    const findFilterArrayDeep = (root) => {
      const seen = new Set();
      const queue = [root];
      let guard = 0;

      while (queue.length && guard++ < 5000) {
        const node = queue.shift();
        if (!node) continue;

        if (typeof node === "object") {
          if (seen.has(node)) continue;
          seen.add(node);
        }

        if (Array.isArray(node)) {
          if (node.length === 0) continue;
        
          if (node.some(looksLikeFilter)) return node;
       
          for (const it of node) queue.push(it);
          continue;
        }

        if (typeof node === "object") {
         
          const fastKeys = ["data", "filters", "items", "content", "result", "payload"];
          for (const k of fastKeys) {
            if (k in node) queue.push(node[k]);
          }
         
          for (const k of Object.keys(node)) {
            if (!fastKeys.includes(k)) queue.push(node[k]);
          }
        }
      }
      return [];
    };

    const normalizedArray = findFilterArrayDeep(response?.data);
    return { data: Array.isArray(normalizedArray) ? normalizedArray : [], status: response.status };
  }
);
export const uploadFileToFTP = createAsyncThunk(
  "/reports/upload",
  async ({ preparedFile, reportName }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const formData = new FormData();
      formData.append("files", preparedFile.blob, preparedFile.name);
      formData.append("type", preparedFile.type);
      formData.append("reportName", reportName);

      const { data, status } = await axios.post(
        `/reports/upload-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearStateBatchReport: (state, { payload }) => {
      state.batchUser = [];
      state.isFetchingBatch = false;
      state.isSuccessBatchUser = false;
      state.isErrorBatchUser = false;
      state.errorMessageBatchUser = "";
    },
    clearStateAbortedReport: (state, { payload }) => {
      state.abortedUser = [];
      state.isFetchingAborted = false;
      state.isSuccessAbortedUser = false;
      state.isErrorAbortedUser = false;
      state.errorMessageAbortedUser = "";
    },
    clearStateDelievrySummaryReport: (state, { payload }) => {
      state.deliverySummaryData = [];
      state.isFetchingDeliverySummary = false;
      state.isSuccessDeliverySummaryData = false;
      state.isErrorDeliverySummaryData = false;
      state.errorMessageDeliverySummaryData = "";
    },
    clearStateContentReport: (state, { payload }) => {
      state.contentUser = [];
      state.cachedGraphData = null;
      state.isFetchingContentUser = false;
      state.isSuccessContentUser = false;
      state.isErrorContentUser = false;
      state.errorMessageContentUser = "";
    },
    clearStateAdvanceReport: (state, { payload }) => {
      state.advanceUser = [];
      state.isFetchingAdvance = false;
      state.isSuccessAdvanceUser = false;
      state.isErrorAdvanceUser = false;
      state.errorMessageAdvanceUser = "";
    },
    clearStateScheduleReport: (state, { payload }) => {
      state.scheduleUser = [];
      state.isFetchingScheduleUser = false;
      state.isSuccessScheduleUser = false;
      state.isErrorScheduleUser = false;
      state.errorMessageScheduleUser = "";
    },
    clearStateLatencyReport: (state, { payload }) => {
      state.latencyUser = [];
      state.isFetchingLatencyUser = false;
      state.isSuccessLatencyUser = false;
      state.isErrorLatencyUser = false;
      state.errorMessageLatencyUser = "";
    },
    clearStateLookupReport: (state, { payload }) => {
      state.lookupUser = [];
      state.isFetchingLookupUser = false;
      state.isSuccessLookupUser = false;
      state.isErrorLookupUser = false;
      state.errorMessageLookupUser = "";
    },
    clearStateSubmissionReport: (state, { payload }) => {
      state.submissionUser = [];
      state.isFetchingSubmissionUser = false;
      state.isSuccessSubmissionUser = false;
      state.isErrorSubmissionUser = false;
      state.errorMessageSubmissionUser = "";
    },
    clearStateConsumptionReport: (state, { payload }) => {
      state.consumptionUser = [];
      state.isFetchingConsumptionUser = false;
      state.isSuccessConsumptionUser = false;
      state.isErrorConsumptionUser = false;
      state.errorMessageConsumptionUser = "";
    },
    clearStateAggregatedReport: (state, { payload }) => {
      state.aggregatedUser = {
        totalTRN: 0,
        totalOTP: 0,
        totalMKT: 0,
        aggregators: [],
      };
      state.isFetchingAggregatedUser = false;
      state.isSuccessAggregatedUser = false;
      state.isErrorAggregatedUser = false;
      state.errorMessageAggregatedUser = "";
    },
    clearStateCampaignReport: (state, { payload }) => {
      state.campaignUser = [];
      state.isFetchingCampaignUser = false;
      state.isSuccessCampaignUser = false;
      state.isErrorCampaignUser = false;
      state.errorMessageCampaignUser = "";
    },
    clearStateSmscReport: (state, { payload }) => {
      state.smscData = [];
      state.isFetchingSmscData = false;
      state.isSuccessSmscData = false;
      state.isErrorSmscData = false;
      state.errorMessageSmscData = "";
    },
    clearStateProfitReport: (state, { payload }) => {
      state.profitData = [];
      state.isFetchingProfitData = false;
      state.isSuccessProfitData = false;
      state.isErrorProfitData = false;
      state.errorMessageProfitData = "";
    },
    clearStatePerformanceUserReport: (state, { payload }) => {
      state.performanceUserData = [];
      state.isFetchingPerformanceUserData = false;
      state.isSuccessPerformanceUser = false;
      state.isErrorPerformanceUser = false;
      state.errorMessagePerformanceUser = "";
    },
    clearStatePerformanceRouteReport: (state, { payload }) => {
      state.performanceRouteData = [];
      state.isFetchingPerformanceRouteData = false;
      state.isSuccessPerformanceRoute = false;
      state.isErrorPerformanceRoute = false;
      state.errorMessagePerformanceRoute = "";
    },
    clearStateCoverageReport: (state, { payload }) => {
      state.coverageData = [];
      state.isFetchingCoverageData = false;
      state.isSuccessCoverage = false;
      state.isErrorCoverage = false;
      state.errorMessageCoverage = "";
    },
    clearStateBlockedReport: (state, { payload }) => {
      state.blockedData = [];
      state.isFetchingBlockedData = false;
      state.isSuccessBlocked = false;
      state.isErrorBlocked = false;
      state.errorMessageBlocked = "";
    },
    clearStateTPSReport: (state, { payload }) => {
      state.TPSData = [];
      state.isFetchingTPSData = false;
      state.isSuccessTPS = false;
      state.isErrorTPS = false;
      state.errorMessageTPS = "";
    },
    clearStateEdrReport: (state, { payload }) => {
      state.edrData = [];
      state.isFetchingEdrData = false;
    },
    clearStateResumeTrafficReport: (state, { payload }) => {
      state.resumeTrafficData = [];
      state.isFetchingResumeTrafficData = false;
    },
  },
  extraReducers: (builder) => {
    builder
    
      .addCase(batchUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.batchUser = action.payload.data[0];
          state.isFetchingBatch = false;
          state.isSuccessBatchUser = true;
          state.isErrorBatchUser = false;
        }
      })
      .addCase(batchUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingBatch = true;
        }
      })
      .addCase(batchUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.batchUser = [];
          state.isSuccessBatchUser = false;
          state.isFetchingBatch = false;
          state.isErrorBatchUser = true;
          state.errorMessageBatchUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

    
      .addCase(abortedUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.abortedUser = action.payload.data[0];
          state.isFetchingAborted = false;
          state.isSuccessAbortedUser = true;
        }
      })
      .addCase(abortedUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingAborted = true;
          state.isErrorAbortedUser = false;
        }
      })
      .addCase(abortedUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.abortedUser = [];
          state.isFetchingAborted = false;
          state.isErrorAbortedUser = true;
          state.isSuccessAbortedUser = false;
          state.errorMessageAbortedUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

      .addCase(deliverySummaryUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.deliverySummaryData = action.payload.data[0];
          state.isFetchingDeliverySummary = false;
          state.isSuccessDeliverySummaryData = true;
        }
      })
      .addCase(deliverySummaryUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingDeliverySummary = true;
        }
      })
      .addCase(deliverySummaryUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.deliverySummaryData = [];
          state.isFetchingDeliverySummary = false;
          state.isErrorDeliverySummaryData = true;
          state.errorMessageDeliverySummaryData = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

    .addCase(contentUserData.fulfilled, (state, action) => {
  if (!action.meta?.arg.download) {
    state.contentUser = action.payload.data[0];
    
    if (action.meta?.arg.firstTime && action.payload.graphData) {
      state.cachedGraphData = action.payload.graphData;
    }
    
    state.isFetchingContentUser = false;
    state.isSuccessContentUser = true;
  }
})
.addCase(contentUserData.pending, (state, action) => {
  if (!action.meta?.arg.download) {
    state.isFetchingContentUser = true;
  }
})
.addCase(contentUserData.rejected, (state, action) => {
  if (!action.meta?.arg.download) {
    state.contentUser = [];
    state.isFetchingContentUser = false;
    state.isErrorContentUser = true;
    state.errorMessageContentUser = action.payload
      ? action.payload.message
      : "Internal error";
  }
})

      .addCase(advanceUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.advanceUser =
            (action.payload?.data && action.payload?.data[0]) || [];
          state.isFetchingAdvance = false;
          state.isSuccessAdvanceUser = true;
        }
      })
      .addCase(advanceUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingAdvance = true;
        }
      })
      .addCase(advanceUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.advanceUser = [];
          state.isFetchingAdvance = false;
          state.isErrorAdvanceUser = true;
          state.errorMessageAdvanceUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

     
      .addCase(scheduleUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.scheduleUser = action.payload.data[0];
          state.isFetchingScheduleUser = false;
          state.isSuccessScheduleUser = true;
          state.isErrorScheduleUser = false;
        }
      })
      .addCase(scheduleUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingScheduleUser = true;
        }
      })
      .addCase(scheduleUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.scheduleUser = [];
          state.isSuccessScheduleUser = false;
          state.isFetchingScheduleUser = false;
          state.isErrorScheduleUser = true;
          state.errorMessageScheduleUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

      
      .addCase(latencyUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
         
          if (action.payload?.status === 404) {
            state.latencyUser = [];
            state.isFetchingLatencyUser = false;
            state.isSuccessLatencyUser = false;
            state.isErrorLatencyUser = true;
            state.errorMessageLatencyUser =
              "Data not found for the selected filters";
          } else {
           
            state.latencyUser = action?.payload ?? [];
            state.isFetchingLatencyUser = false;
            state.isSuccessLatencyUser = true;
            state.isErrorLatencyUser = false;
            state.errorMessageLatencyUser = null;
          }
        }
      })
      .addCase(latencyUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingLatencyUser = true;
          state.isErrorLatencyUser = false;
          state.errorMessageLatencyUser = null;
        }
      })
      .addCase(latencyUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.latencyUser = [];
          state.isSuccessLatencyUser = false;
          state.isFetchingLatencyUser = false;
          state.isErrorLatencyUser = true;
          state.errorMessageLatencyUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

    
      .addCase(lookupUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.lookupUser = action.payload.data;
          state.isFetchingLookupUser = false;
          state.isSuccessLookupUser = true;
          state.isErrorLookupUser = false;
        }
      })
      .addCase(lookupUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingLookupUser = true;
        }
      })
      .addCase(lookupUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.lookupUser = [];
          state.isSuccessLookupUser = false;
          state.isFetchingLookupUser = false;
          state.isErrorLookupUser = true;
          state.errorMessageLookupUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

    
      .addCase(submissionUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.submissionUser = action.payload.data;
          state.isFetchingSubmissionUser = false;
          state.isSuccessSubmissionUser = true;
          state.isErrorSubmissionUser = false;
        }
      })
      .addCase(submissionUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingSubmissionUser = true;
        }
      })
      .addCase(submissionUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.submissionUser = [];
          state.isSuccessSubmissionUser = false;
          state.isFetchingSubmissionUser = false;
          state.isErrorSubmissionUser = true;
          state.errorMessageSubmissionUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

      .addCase(consumptionUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.consumptionUser = action.payload.data;
          state.isFetchingConsumptionUser = false;
          state.isSuccessConsumptionUser = true;
        }
      })
      .addCase(consumptionUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingConsumptionUser = true;
        }
      })
      .addCase(consumptionUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.consumptionUser = [];
          state.isFetchingConsumptionUser = false;
          state.isErrorConsumptionUser = true;
          state.errorMessageConsumptionUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

   
      .addCase(aggregatedReportData.fulfilled, (state, action) => {
    if (!action.meta?.arg.download) {
    
      state.aggregatedUser = action.payload.data[0] || [];
      state.isFetchingAggregatedUser = false;
      state.isSuccessAggregatedUser = true;
    }
  })
      .addCase(aggregatedReportData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingAggregatedUser = true;
        }
      })
      .addCase(aggregatedReportData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.aggregatedUser = [];
          state.isFetchingAggregatedUser = false;
          state.isErrorAggregatedUser = true;
          state.errorMessageAggregatedUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

  
      .addCase(campaignUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.campaignUser = action.payload.data;
          state.isFetchingCampaignUser = false;
          state.isSuccessCampaignUser = true;
          state.isErrorCampaignUser = false;
        }
      })
      .addCase(campaignUserData.pending, (state, action) => {
        if (!action.meta?.arg?.download) {
          state.isFetchingCampaignUser = true;
          state.campaignUser = [];
        }
      })
      .addCase(campaignUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.campaignUser = [];
          state.isFetchingCampaignUser = false;
          state.isErrorCampaignUser = true;
          state.isSuccessCampaignUser = false;
          state.errorMessageCampaignUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

      
      .addCase(getSmscData.fulfilled, (state, action) => {
        if (!action.meta?.arg?.download) {
          state.smscData = action.payload?.data;
          state.isFetchingSmscData = false;
          state.isSuccessSmscData = true;
          state.isErrorSmscData = false;
        }
      })
      .addCase(getSmscData.pending, (state, action) => {
        if (!action.meta?.arg?.download) {
          state.isFetchingSmscData = true;
        }
      })
      .addCase(getSmscData.rejected, (state, action) => {
        if (!action.meta?.arg?.download) {
          state.smscData = [];
          state.isFetchingSmscData = false;
          state.isErrorSmscData = true;
          state.isSuccessSmscData = false;
          state.errorMessageSmscData = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

     
      .addCase(getSmscList.fulfilled, (state, action) => {
        state.smscListData = action.payload;
        state.isFetchingSmscList = false;
        state.isSuccessSmscList = true;
        state.isErrorSmscList = false;
      })
      .addCase(getSmscList.pending, (state) => {
        state.isFetchingSmscList = true;
      })
      .addCase(getSmscList.rejected, (state, action) => {
        state.smscListData = [];
        state.isFetchingSmscList = false;
        state.isErrorSmscList = true;
        state.isSuccessSmscList = false;
        state.errorMessageSmscList = action.payload
          ? action.payload.message
          : "Internal error";
      })

     
      .addCase(getProfitData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.profitData = action.payload.data;
          state.isFetchingProfitData = false;
          state.isSuccessProfitData = true;
          state.isErrorProfitData = false;
        }
      })
      .addCase(getProfitData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingProfitData = true;
        }
      })
      .addCase(getProfitData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.profitData = [];
          state.isFetchingProfitData = false;
          state.isErrorProfitData = true;
          state.isSuccessProfitData = false;
          state.errorMessageProfitData = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })

      
      .addCase(getPerformanceUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.performanceUserData = action.payload.data[0];
          state.isFetchingPerformanceUserData = false;
          state.isSuccessPerformanceUser = true;
          state.isErrorPerformanceUser = false;
        }
      })
      .addCase(getPerformanceUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingPerformanceUserData = true;
        }
      })
      .addCase(getPerformanceUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.performanceUserData = [];
          state.isFetchingPerformanceUserData = false;
          state.isErrorPerformanceUser = true;
          state.isSuccessPerformanceUser = false;
          state.errorMessagePerformanceUser = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })
      
      .addCase(getPerformanceRouteData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.performanceRouteData = action.payload.data[0];
          state.isFetchingPerformanceRouteData = false;
          state.isSuccessPerformanceRoute = true;
          state.isErrorPerformanceRoute = false;
        }
      })
      .addCase(getPerformanceRouteData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingPerformanceRouteData = true;
        }
      })
      .addCase(getPerformanceRouteData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.performanceRouteData = [];
          state.isFetchingPerformanceRouteData = false;
          state.isErrorPerformanceRoute = true;
          state.isSuccessPerformanceRoute = false;
          state.errorMessagePerformanceRoute = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })
      .addCase(getBlockedData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.blockedData = action.payload.data[0];
          state.isFetchingBlockedData = false;
          state.isSuccessBlocked = true;
          state.isErrorBlocked = false;
        }
      })
      .addCase(getBlockedData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingBlockedData = true;
        }
      })
      .addCase(getBlockedData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.blockedData = [];
          state.isFetchingBlockedData = false;
          state.isErrorBlocked = true;
          state.isSuccessBlocked = false;
          state.errorMessageBlocked = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })
      .addCase(getTPSData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.TPSData = action.payload.content;
          state.isFetchingTPSData = false;
        }
      })
      .addCase(getTPSData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingTPSData = true;
        }
      })
      .addCase(getTPSData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.TPSData = [];
          state.isFetchingTPSData = false;
        }
      })
      .addCase(getCoverageUserData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.coverageData = Array.isArray(action.payload.data)
            ? action.payload.data
            : [];
          state.isFetchingCoverageData = false;
          state.isSuccessCoverage = true;
          state.isErrorCoverage = false;
        }
      })
      .addCase(getCoverageUserData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingCoverageData = true;
        }
      })
      .addCase(getCoverageUserData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.coverageData = [];
          state.isFetchingCoverageData = false;
          state.isErrorCoverage = true;
          state.isSuccessCoverage = false;
          state.errorMessageCoverage = action.payload
            ? action.payload.message
            : "Internal error";
        }
      })
      .addCase(getEdrData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.edrData = action.payload.data;
          state.isFetchingEdrData = false;
        }
      })
      .addCase(getEdrData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingEdrData = true;
        }
      })
      .addCase(getEdrData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.edrData = [];
          state.isFetchingEdrData = false;
        }
      })
      .addCase(getResumeTrafficData.fulfilled, (state, action) => {
        if (!action.meta?.arg.download) {
          state.resumeTrafficData = action.payload.data;
          state.isFetchingResumeTrafficData = false;
        }
      })
      .addCase(getResumeTrafficData.pending, (state, action) => {
        if (!action.meta?.arg.download) {
          state.isFetchingResumeTrafficData = true;
        }
      })
      .addCase(getResumeTrafficData.rejected, (state, action) => {
        if (!action.meta?.arg.download) {
          state.resumeTrafficData = [];
          state.isFetchingResumeTrafficData = false;
        }
      })
      .addCase(saveFilter.pending, (state) => {
        state.isFetchingSaveFilter = true;
      })
      .addCase(saveFilter.rejected, (state) => {
        state.isFetchingSaveFilter = false;
      })
      .addCase(saveFilter.fulfilled, (state) => {
        state.isFetchingSaveFilter = false;
      });
  },
});

export const {
  clearStateBatchReport,
  clearStateAbortedReport,
  clearStateDelievrySummaryReport,
  clearStateContentReport,
  clearStateAdvanceReport,
  clearStateLatencyReport,
  clearStateLookupReport,
  clearStateSubmissionReport,
  clearStateScheduleReport,
  clearStateConsumptionReport,
  clearStateAggregatedReport,
  clearStateCampaignReport,
  clearStateSmscReport,
  clearStateProfitReport,
  clearStatePerformanceUserReport,
  clearStatePerformanceRouteReport,
  clearStateCoverageReport,
  clearStateBlockedReport,
  clearStateTPSReport,
  clearStateEdrReport,
  clearStateResumeTrafficReport,
} = reportSlice.actions;

export function clearStateBatch() {
  return async (dispatch) => {
    dispatch(clearStateBatchReport());
  };
}

export function clearStateAborted() {
  return async (dispatch) => {
    dispatch(clearStateAbortedReport());
  };
}

export function clearStateDeliverySummary() {
  return async (dispatch) => {
    dispatch(clearStateDelievrySummaryReport());
  };
}

export function clearStateContent() {
  return async (dispatch) => {
    dispatch(clearStateContentReport());
  };
}

export function clearStateAdvance() {
  return async (dispatch) => {
    dispatch(clearStateAdvanceReport());
  };
}

export function clearStateSchedule() {
  return async (dispatch) => {
    dispatch(clearStateScheduleReport());
  };
}

export function clearStateLatency() {
  return async (dispatch) => {
    dispatch(clearStateLatencyReport());
  };
}

export function clearStateLookup() {
  return async (dispatch) => {
    dispatch(clearStateLookupReport());
  };
}

export function clearStateSubmission() {
  return async (dispatch) => {
    dispatch(clearStateSubmissionReport());
  };
}

export function clearStateConsumption() {
  return async (dispatch) => {
    dispatch(clearStateConsumptionReport());
  };
}

export function clearStateAggregated() {
  return async (dispatch) => {
    dispatch(clearStateAggregatedReport());
  };
}

export function clearStateCampaign() {
  return async (dispatch) => {
    dispatch(clearStateCampaignReport());
  };
}

export function clearStateSmsc() {
  return async (dispatch) => {
    dispatch(clearStateSmscReport());
  };
}

export function clearStateProfit() {
  return async (dispatch) => {
    dispatch(clearStateProfitReport());
  };
}
export function clearStatePerformanceUser() {
  return async (dispatch) => {
    dispatch(clearStatePerformanceUserReport());
  };
}
export function clearStateCoverage() {
  return async (dispatch) => {
    dispatch(clearStateCoverageReport());
  };
}

export const reportSelector = (state) => state.report;

export default reportSlice.reducer;
